import { Controller, type ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Combobox,
  Label,
  MultiSelect,
  RadioGroup,
  RichTextRenderer,
  type ComboboxOption,
  type MultiSelectOption
} from '@knack/asterisk-react';

type MultipleChoiceBaseOption = {
  label: string;
  value: string;
};

type MultiChoiceBaseInputProps = {
  blankOption?: string;
  hasBlankOption?: boolean;
  options: MultipleChoiceBaseOption[];
  formValueKey: string;
  multiType: 'one' | 'many';
  multiInput: 'radio' | 'checkbox' | 'chosen';
  intent: 'default' | 'destructive';
  disabled?: boolean;
};

export function MultiChoiceBaseInput({
  blankOption,
  hasBlankOption = false,
  options,
  formValueKey,
  multiType,
  multiInput,
  intent,
  disabled
}: MultiChoiceBaseInputProps) {
  const [t] = useTranslation();

  const placeholder = hasBlankOption
    ? blankOption
    : t('components.views.form.multiple_choice.select');

  function handleCheckboxChange(
    option: MultipleChoiceBaseOption,
    fieldValue: ControllerRenderProps['value'],
    onChange: ControllerRenderProps['onChange']
  ) {
    if (fieldValue.includes(option.value)) {
      onChange(fieldValue.filter((item: string) => item !== option.value));
    } else {
      onChange([...fieldValue, option.value]);
    }
  }

  function handleMultiSelectChange(
    selectedValues: MultiSelectOption[],
    onChange: ControllerRenderProps['onChange']
  ) {
    onChange(selectedValues.map((val) => val.value));
  }

  function handleSingleSelectChange(
    option: ComboboxOption,
    onChange: ControllerRenderProps['onChange']
  ) {
    if (option.key === 'kn-blank' || option.key === blankOption) {
      onChange('');
      return;
    }
    onChange(option.key);
  }

  if (multiType === 'one' && multiInput === 'radio') {
    return (
      <Controller
        name={formValueKey}
        render={({ field: { value, onChange } }) => (
          <RadioGroup value={value} onValueChange={onChange} disabled={disabled}>
            {options.map((option, index) => {
              const radioInputId = `radio-${index}`;
              return (
                <RadioGroup.Container key={radioInputId}>
                  <RadioGroup.Item value={option.value} id={radioInputId} />
                  <Label htmlFor={radioInputId}>
                    <RichTextRenderer dangerouslySetInnerHTML={{ __html: option.label }} />
                  </Label>
                </RadioGroup.Container>
              );
            })}
          </RadioGroup>
        )}
      />
    );
  }

  if (multiType === 'many' && multiInput === 'checkbox') {
    return (
      <Controller
        name={formValueKey}
        render={({ field: { value, onChange } }) => (
          <>
            {options.map((option, index) => {
              const checkboxesInputId = `checkbox-${index}`;
              return (
                <div className="flex items-center gap-2" key={checkboxesInputId}>
                  <Checkbox
                    checked={value?.includes(option.value)}
                    id={checkboxesInputId}
                    onClick={() => {
                      handleCheckboxChange(option, value, onChange);
                    }}
                  />
                  <Label htmlFor={checkboxesInputId}>
                    <RichTextRenderer dangerouslySetInnerHTML={{ __html: option.label }} />
                  </Label>
                </div>
              );
            })}
          </>
        )}
      />
    );
  }

  if (multiType === 'many' && multiInput === 'chosen') {
    const formattedInputOptions: MultiSelectOption[] = options.map((option) => ({
      label: option.label,
      value: option.value,
      key: option.value
    }));

    return (
      <Controller
        name={formValueKey}
        render={({ field: { value, onChange } }) => {
          const selectedOptions: MultiSelectOption[] = [];

          if (Array.isArray(value)) {
            value.forEach((val: string) => {
              const formattedOption = formattedInputOptions.find((option) => option.value === val);

              if (!formattedOption) {
                return;
              }

              selectedOptions.push({
                label: formattedOption?.label,
                value: formattedOption?.value,
                key: formattedOption?.key
              });
            });
          }

          return (
            <MultiSelect
              disabled={disabled}
              intent={intent}
              id="multiselect"
              options={formattedInputOptions}
              selectedOptions={selectedOptions}
              maxVisibleChips={5}
              isSearchEnabled
              onSelectOptions={(values) => handleMultiSelectChange(values, onChange)}
            />
          );
        }}
      />
    );
  }

  if (multiType === 'one' && multiInput === 'chosen') {
    return (
      <Controller
        name={formValueKey}
        render={({ field: { value, onChange } }) => {
          const opts =
            hasBlankOption && options
              ? [{ label: blankOption, value: blankOption }, ...options]
              : options;
          const formattedOptions = opts.map((option) => ({
            key: option.value || '',
            label: option.label || ''
          }));

          const val = Array.isArray(value) ? value[0] : value;
          const selectedOptionFormatted = formattedOptions.find((opt) => opt.key === val);
          const selectedOption =
            val === 'kn-blank'
              ? { key: 'kn-blank', label: '' }
              : {
                  key: selectedOptionFormatted?.key || '',
                  label: selectedOptionFormatted?.label || ''
                };

          return (
            <Combobox
              disabled={disabled}
              id="combobox-single-multiple-choice"
              placeholder={placeholder}
              intent={intent}
              options={formattedOptions}
              selectedOption={selectedOption}
              addOptionText={t('actions.add')}
              onSelectOption={(option) => handleSingleSelectChange(option, onChange)}
            />
          );
        }}
      />
    );
  }

  return null;
}
